import React, { useState } from "react";
import { DV2 } from "../theme";
import { euros, hours as formatHours, plural } from "../format";
import { vocab } from "../vocabulary";
import { Card, KeyValue, TextLink, useDv2Theme } from "../layout/Ui";
import { DeclarationAmounts, MonthlyCeiling } from "./amounts";
import { isVerifiedTrainer } from "../../../Utils/VerifiedTrainerAmounts";

interface Props {
    amounts: DeclarationAmounts;
    /** « pour ces 3 séances » / « pour ce cours ». */
    scope: string;
    showDeposit: boolean;
}

/**
 * Bloc de montants unique (maquettes 1d, 1k, 2d, 2k) : ce que le prof
 * reçoit, ce que le client paie, la part de l'avance immédiate, l'acompte et
 * le reste à payer, avec l'explication du calcul dépliable.
 */
export const AmountsBlock = ({ amounts, scope, showDeposit }: Props) => {
    const { primary, fontFamily, brandName } = useDv2Theme();
    const [explain, setExplain] = useState(false);
    const a = amounts;
    const hasPrice = a.price > 0 && a.hours > 0;
    // Coopérative : chez Teach'r, ce n'est pas Teach'r qui facture le client et reverse la part du prof mais la
    // coopérative partenaire (Verified Trainer SAP) ; chez Verified Trainer, la marque elle-même.
    const coopIssuer = isVerifiedTrainer() ? brandName : "la coopérative partenaire";
    const coopIssuerSentence = isVerifiedTrainer() ? brandName : "La coopérative partenaire";
    const value = (n: number) => (hasPrice ? euros(n) : "—".replace("—", "…"));

    return (
        <Card padding={0} style={{ overflow: "hidden", fontFamily }} aria-label="Montants">
            <div style={{ padding: 22, background: primary, color: "#fff", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.85 }}>Vous recevez {scope}</div>
                <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.1 }} aria-live="polite">{value(a.received)}</div>
                <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45 }}>
                    {hasPrice ? (
                        a.coop
                            ? <>soit {euros(a.receivedHt)} hors TVA{a.vatSubject ? ` et ${euros(a.receivedVat)} de TVA` : ""}, reversés par {coopIssuer}.</>
                            : <>soit {euros(a.receivedHourly)} par heure, reversés par {brandName}{a.vatSubject ? `, TVA comprise (${euros(a.receivedVat)})` : ""}.</>
                    ) : "Saisissez le prix horaire payé."}
                    {showDeposit && a.deposit > 0 && " L'acompte ne change pas ce montant."}
                </div>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14, fontSize: 15 }}>
                <KeyValue label={`Montant total ${vocab.ofTheCourse()}`} value={<>{value(a.totalTtc)} TTC</>} />
                <KeyValue label="Pris en charge par l'avance immédiate" value={<span style={{ color: DV2.success }}>{value(a.advance)}</span>} />
                {showDeposit && a.deposit > 0 && <KeyValue label="Acompte déjà versé" value={<span style={{ color: DV2.success }}>{euros(a.deposit)}</span>} />}
                <div style={{ paddingTop: 12, borderTop: `1px solid ${DV2.borderSoft}` }}>
                    <KeyValue label="Reste à payer pour le client" value={value(a.remaining)} />
                </div>
                <div>
                    <TextLink label={explain ? "Masquer le détail du calcul" : "Comment ces montants sont calculés"} onClick={() => setExplain((e) => !e)} />
                </div>
                {explain && (
                    <div style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.55, background: DV2.pageBg, borderRadius: 12, padding: "12px 14px" }}>
                        {a.coop ? (
                            <>
                                Le prix total {vocab.ofTheCourse()} est de <b>{euros(a.totalTtc)}</b> TTC ({euros(a.price * 2)} par heure sur {formatHours(a.hours)}).
                                Votre {vocab.client()} n'en paie que la moitié, <b>{euros(a.advance)}</b>, l'autre moitié est avancée par l'URSSAF.
                                {coopIssuerSentence} facture ce montant, soit {euros(a.totalHt)} hors TVA (TVA à {a.territorialRate} %).
                                Après commissions ({euros(a.fees)}), votre part est de <b>{euros(a.receivedHt)}</b> hors TVA
                                {a.vatSubject ? <>, soit <b>{euros(a.received)}</b> avec votre TVA ({euros(a.receivedVat)}) ajoutée par-dessus.</> : "."}
                            </>
                        ) : (
                            <>
                                Vous saisissez <b>{euros(a.price)}</b> par heure : c'est ce que paie votre {vocab.client()} après avance immédiate.
                                Le prix total facturé est donc de <b>{euros(a.price * 2)}</b> par heure, dont la moitié est avancée par l'URSSAF.
                                Les frais de gestion de {brandName} sont de {Math.round(a.feesRate * 1000) / 10} % du montant facturé, soit {euros(a.price * 2 * a.feesRate)} par heure :
                                vous recevez <b>{euros(a.receivedHourly)}</b> par heure, {euros(a.received)} pour {formatHours(a.hours)}.
                                {a.vatSubject ? <> Ce montant comprend votre TVA : {euros(a.receivedVat)} au total, soit {euros(a.receivedHt)} hors TVA.</> : ""}
                            </>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};

interface CeilingProps {
    ceiling: MonthlyCeiling;
    firstName: string;
    isVt: boolean;
    /**
     * Client en relation Teach'r : le plafond compte la part de la plateforme,
     * que le prof ne doit pas connaître. Aucun montant en euros, seulement la
     * barre et l'alerte de dépassement.
     */
    hideAmounts?: boolean;
}

/** « Plafond du mois pour Ben », barre à deux segments (déjà déclaré, cette déclaration). */
export const CeilingPanel = ({ ceiling, firstName, isVt, hideAmounts = false }: CeilingProps) => {
    const { primary, fontFamily } = useDv2Theme();
    const c = ceiling;
    const used = Math.round(Math.min(1, c.ceiling > 0 ? c.total / c.ceiling : 0) * 100);
    return (
        <Card style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily, borderColor: c.exceeds ? DV2.danger : undefined }} aria-label={`Plafond du mois pour ${firstName}`}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14 }}>
                <span style={{ fontWeight: 800 }}>Plafond du mois pour {firstName}</span>
                <span style={{ color: c.exceeds ? DV2.danger : DV2.muted, fontWeight: c.exceeds ? 800 : 500 }}>
                    {hideAmounts ? (c.exceeds ? "dépassé" : `${used} % utilisé`) : <>{euros(c.total)} sur {euros(c.ceiling)}{isVt ? " TTC" : ""}</>}
                </span>
            </div>
            <div aria-hidden style={{ height: 8, borderRadius: 4, background: "#E3EAF2", overflow: "hidden", display: "flex" }}>
                <div style={{ width: `${c.declaredRatio * 100}%`, background: primary }} />
                <div style={{ width: `${c.thisRatio * 100}%`, background: c.exceeds ? DV2.danger : "#7FB2E5" }} />
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: DV2.muted, flexWrap: "wrap" }}>
                <span><span aria-hidden style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: primary, marginRight: 6 }} />Déjà déclaré{hideAmounts ? "" : ` ${euros(c.declared, 0)}`}</span>
                <span><span aria-hidden style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: c.exceeds ? DV2.danger : "#7FB2E5", marginRight: 6 }} />Cette déclaration{hideAmounts ? "" : ` ${euros(c.thisDeclaration)}`}</span>
            </div>
            {hideAmounts && !c.exceeds && (
                <div style={{ fontSize: 12, color: DV2.muted, lineHeight: 1.45 }}>
                    Plafond de l'avance immédiate pour {firstName} ce mois, tous prestataires confondus.
                </div>
            )}
            {c.exceeds && (
                <div role="alert" style={{ fontSize: 13, color: DV2.danger, fontWeight: 700, lineHeight: 1.45 }}>
                    {hideAmounts
                        ? "Plafond mensuel de l'avance immédiate dépassé pour cet élève : retirez des séances ou déplacez-les sur le mois suivant."
                        : "Plafond mensuel dépassé : baissez le prix, retirez des séances ou déplacez-les sur le mois suivant."}
                </div>
            )}
        </Card>
    );
};

export const scopeLabel = (sessionsCount: number): string =>
    sessionsCount > 1 ? `pour ces ${plural(sessionsCount, "séance")}` : `pour ${vocab.thisCourse()}`;
